// src/pages/ServerEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API_BASE } from "../lib/config";

export default function ServerEdit() {
  const { serverId } = useParams(); // can be numeric id OR server_number
  const navigate = useNavigate();

  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const isNumeric = /^\d+$/.test(serverId);
      const url = isNumeric
        ? `${API_BASE}/servers/${serverId}`
        : `${API_BASE}/servers/by-number/${encodeURIComponent(serverId)}`;

      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error(`Failed to load (HTTP ${r.status})`);
      const data = await r.json();
      setServer(data); // includes numeric id
    } catch (e) {
      setErr(e.message || "Failed to load server.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  function onChange(e) {
    const { name, value } = e.target;
    setServer((s) => ({ ...s, [name]: value }));
  }

  async function onSave(e) {
    e.preventDefault();
    if (!server?.id) return;
    setSaving(true);
    setErr("");
    try {
      const r = await fetch(`${API_BASE}/servers/${server.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: server.name,
          company: server.company,
          phone: server.phone,
          email: server.email,
          service_area: server.service_area,
          license_number: server.license_number,
          status: server.status,
          payment_terms: server.payment_terms,
          notes_summary: server.notes_summary,
          // server_number is read-only by default (omit unless you want to allow override)
        }),
      });
      if (!r.ok) throw new Error(`Save failed (HTTP ${r.status})`);
      await load();
    } catch (e) {
      setErr(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  if (err) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: "crimson", marginBottom: 12 }}>{err}</div>
        <button onClick={load} style={{ marginRight: 10 }}>Retry</button>
        <Link to="/servers">Back to Process Servers</Link>
      </div>
    );
  }

  if (!server) return null;

  const input = { width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 8 };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      {/* Back links */}
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => navigate(-1)} style={{ marginRight: 12 }}>← Back</button>
        <Link to="/servers">Process Servers</Link>
      </div>

      <h2 style={{ marginTop: 0 }}>Edit Process Server</h2>
      <div style={{ color: "#777", marginBottom: 16 }}>
        Server #: <b>{server.server_number || "—"}</b> &nbsp; · &nbsp; ID: {server.id}
      </div>

      <form onSubmit={onSave} style={{ display: "grid", gap: 12 }}>
        <label>
          <div>Name</div>
          <input name="name" value={server.name || ""} onChange={onChange} style={input} />
        </label>
        <label>
          <div>Company</div>
          <input name="company" value={server.company || ""} onChange={onChange} style={input} />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div>Phone</div>
            <input name="phone" value={server.phone || ""} onChange={onChange} style={input} />
          </label>
          <label>
            <div>Email</div>
            <input name="email" value={server.email || ""} onChange={onChange} style={input} />
          </label>
        </div>
        <label>
          <div>Service Area</div>
          <input name="service_area" value={server.service_area || ""} onChange={onChange} style={input} />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div>License #</div>
            <input name="license_number" value={server.license_number || ""} onChange={onChange} style={input} />
          </label>
          <label>
            <div>Status</div>
            <select name="status" value={server.status || "ACTIVE"} onChange={onChange} style={input}>
              <option>ACTIVE</option>
              <option>INACTIVE</option>
              <option>ON_LEAVE</option>
            </select>
          </label>
        </div>
        <label>
          <div>Payment Terms</div>
          <input name="payment_terms" value={server.payment_terms || ""} onChange={onChange} style={input} />
        </label>
        <label>
          <div>Notes (summary)</div>
          <textarea
            name="notes_summary"
            value={server.notes_summary || ""}
            onChange={onChange}
            style={{ ...input, minHeight: 100 }}
          />
        </label>

        {/* Read-only server_number */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div>Server # (read-only)</div>
            <input value={server.server_number || ""} readOnly style={{ ...input, background: "#f9fafb" }} />
          </label>
        </div>

        <div>
          <button type="submit" disabled={saving} style={{ padding: "8px 14px", borderRadius: 8 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        {err && <div style={{ color: "crimson" }}>{err}</div>}
      </form>
    </div>
  );
}
